import {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    HubConnectionBuilder,
    HubConnectionState,
    ILogger,
    LogLevel,
} from '@microsoft/signalr';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import { useIdentity } from '../auth/useIdentity';
import { useAxiosSwagger } from './useAxiosSwagger';
import {
    acknowledgeAlerts,
    Alert,
    ALERT_METHOD,
    alertHubUrl,
    getAlerts,
} from '../api/alerts';

/** Remembered per browser: the newest alert this person has seen. */
const SEEN_KEY = 'alerts:lastSeen';

/** How long a toast stays on screen. */
const TOAST_MS = 9000;

/** Once a minute as a fallback, for when the websocket does not get through. */
const POLL_MS = 60000;

const quietLogger: ILogger = {
    log(logLevel, message) {
        if (logLevel < LogLevel.Warning || message.includes('stopped during negotiation')) {
            return;
        }

        // eslint-disable-next-line no-console
        console.warn(`[alert-hub] ${message}`);
    },
};

const readSeen = (): string => {
    try {
        return window.localStorage.getItem(SEEN_KEY) ?? '';
    } catch {
        return '';
    }
};

const writeSeen = (value: string) => {
    try {
        window.localStorage.setItem(SEEN_KEY, value);
    } catch {
        // A private window: the count simply starts over next time.
    }
};

export type BrowserNotifications = 'unsupported' | 'default' | 'granted' | 'denied';

export interface AlertsState {
    alerts: Alert[];
    /** Alerts newer than the newest one this browser has seen. */
    unread: number;
    toasts: Alert[];
    live: boolean;
    notifications: BrowserNotifications;
    markSeen: () => void;
    dismissToast: (id: string) => void;
    acknowledge: (ids: string[]) => Promise<void>;
    enableNotifications: () => Promise<void>;
    open: (alert: Alert) => void;
}

const empty: AlertsState = {
    alerts: [],
    unread: 0,
    toasts: [],
    live: false,
    notifications: 'unsupported',
    markSeen: () => undefined,
    dismissToast: () => undefined,
    acknowledge: async () => undefined,
    enableNotifications: async () => undefined,
    open: () => undefined,
};

const AlertsContext = createContext<AlertsState>(empty);

const permission = (): BrowserNotifications =>
    typeof window !== 'undefined' && 'Notification' in window
        ? (Notification.permission as BrowserNotifications)
        : 'unsupported';

/**
 * The alerts of the backlog for the whole app: loaded once, then pushed live by the alert
 * hub. A new alert shows as a toast on whatever page is open and, once allowed, as a
 * notification of the browser itself - also when the dashboard is in a tab in the background.
 *
 * Only for admins, like the backlog the alerts are about; for anyone else this provides
 * nothing and connects to nothing.
 */
export const AlertsProvider = ({ children }: { children: ReactNode }) => {
    const axios = useAxiosSwagger();
    const { getAccessTokenSilently } = useAuth();
    const { isAdmin } = useIdentity();
    const navigate = useNavigate();

    const [alerts, setAlerts] = useState<Alert[]>([]);
    const [toasts, setToasts] = useState<Alert[]>([]);
    const [seen, setSeen] = useState<string>(readSeen);
    const [live, setLive] = useState<boolean>(false);
    const [notifications, setNotifications] = useState<BrowserNotifications>(permission);
    const known = useRef<Set<string>>(new Set());

    const open = useCallback(
        (alert: Alert) => {
            if (alert.link) {
                navigate(alert.link);
            }
        },
        [navigate]
    );

    const announce = useCallback(
        (alert: Alert) => {
            setToasts((current) => [alert, ...current].slice(0, 4));
            window.setTimeout(
                () => setToasts((current) => current.filter((t) => t.id !== alert.id)),
                TOAST_MS
            );

            if (permission() === 'granted') {
                try {
                    const note = new Notification(alert.title, {
                        body: alert.message ?? undefined,
                        tag: alert.id,
                    });

                    note.onclick = () => {
                        window.focus();
                        open(alert);
                        note.close();
                    };
                } catch {
                    // Some browsers only allow notifications from a service worker; the
                    // toast is there either way.
                }
            }
        },
        [open]
    );

    const receive = useCallback(
        (incoming: Alert[], announceNew: boolean) => {
            const fresh = incoming.filter((a) => !known.current.has(a.id));

            fresh.forEach((a) => known.current.add(a.id));

            if (fresh.length === 0) {
                return;
            }

            setAlerts((current) =>
                [...fresh, ...current]
                    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
                    .slice(0, 200)
            );

            if (announceNew) {
                fresh.forEach(announce);
            }
        },
        [announce]
    );

    useEffect(() => {
        if (!axios || !isAdmin) {
            return;
        }

        let cancelled = false;

        const load = (announceNew: boolean) =>
            getAlerts(axios, 100)
                .then((list) => !cancelled && receive(list, announceNew))
                .catch(() => undefined);

        // The first load is history: counted as unread, not announced.
        load(false);

        const poll = window.setInterval(() => load(true), POLL_MS);

        const hub = new HubConnectionBuilder()
            .withUrl(alertHubUrl, { accessTokenFactory: () => getAccessTokenSilently() })
            .withAutomaticReconnect()
            .configureLogging(quietLogger)
            .build();

        hub.on(ALERT_METHOD, (alert: Alert) => !cancelled && receive([alert], true));
        hub.onreconnected(() => !cancelled && setLive(true));
        hub.onreconnecting(() => !cancelled && setLive(false));
        hub.onclose(() => !cancelled && setLive(false));

        hub.start()
            .then(() => !cancelled && setLive(true))
            .catch(() => !cancelled && setLive(false));

        return () => {
            cancelled = true;
            window.clearInterval(poll);

            if (hub.state !== HubConnectionState.Disconnected) {
                hub.stop().catch(() => undefined);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axios, isAdmin]);

    const markSeen = useCallback(() => {
        const newest = alerts[0]?.createdAt ?? '';

        setSeen(newest);
        writeSeen(newest);
    }, [alerts]);

    const acknowledge = useCallback(
        async (ids: string[]) => {
            if (!axios || ids.length === 0) {
                return;
            }

            await acknowledgeAlerts(axios, ids);

            const now = new Date().toISOString();
            setAlerts((current) =>
                current.map((a) => (ids.includes(a.id) && !a.acknowledgedAt ? { ...a, acknowledgedAt: now } : a))
            );
        },
        [axios]
    );

    const enableNotifications = useCallback(async () => {
        if (permission() === 'unsupported') {
            return;
        }

        setNotifications((await Notification.requestPermission()) as BrowserNotifications);
    }, []);

    const value = useMemo<AlertsState>(
        () => ({
            alerts,
            unread: alerts.filter((a) => a.createdAt > seen && !a.acknowledgedAt).length,
            toasts,
            live,
            notifications,
            markSeen,
            dismissToast: (id: string) => setToasts((current) => current.filter((t) => t.id !== id)),
            acknowledge,
            enableNotifications,
            open,
        }),
        [alerts, seen, toasts, live, notifications, markSeen, acknowledge, enableNotifications, open]
    );

    return <AlertsContext.Provider value={value}>{children}</AlertsContext.Provider>;
};

export const useAlerts = () => useContext(AlertsContext);
